import { toast } from "react-toastify";
import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import { allowedNavOptions, checkAccessTypeFromCookie } from "./userManagement";
import { deletePaginationCookies } from "./utils";

let controller = new AbortController();

export function deleteCookie(cookieName, includePath = true) {
  if (typeof includePath !== "undefined" && includePath === false) {
    // Set the cookie with the same name and path to delete it
    document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
  } else {
    // Set the cookie with the same name and path to delete it
    document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

}

export function createCookie(cookieName, cookieValue) {
  let date = new Date();
  date.setTime(date.getTime() + 2 * 60 * 60 * 1000); //cookie will be valid for 2 hours
  document.cookie = cookieName + "=" + cookieValue + "; expires = " + date.toGMTString() + "; path=/;" + "; Secure; SameSite=Strict";
}

//fetch auth token from cookies
export const fetchToken = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("authToken")) !== "undefined") {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("authToken"))
      .split("=")[1];
  }
  return "";
};

export const fetchBrowserNotificationCookie = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("browserNotification")) !== "undefined") {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("browserNotification"))
      .split("=")[1];
  }
  return "";
};

// to logout
export const logout = () => {
  deleteCookie("authToken");
  deleteCookie("userPermissions");
  deleteCookie("workspace_options");
  deleteCookie("current_workspace");
  deleteCookie("userRole");
  deleteCookie("userDetail");
  deleteCookie("browserNotification");
  deleteCookie("access_types");
  deletePaginationCookies();
  window.location.href = "/";
};

export const setLoadingStatus = (status) => {
  //update redux state to start a loader whenever a new request is send from the application
  store.dispatch(updateStateData({ loading: status }));
};

export const setBatchLoadingStatus = (status) => {
  //update redux state to start a loader whenever a new request is send from the application
  //console.trace("Batchload triggered to: " + status )
  store.dispatch(updateStateData({ batchLoading: status }));
};

export const sendApiRequestWithFile = async (apiPath, requestBody, requestMethod) => {
  // re-initialize the controller if the previous request was aborted
  if (controller.signal.aborted) {
    controller = new AbortController();
  }
  const authToken = fetchToken();
  const requestHeaders = new Headers();
  //auth token must be added to headers object (If available)
  if (authToken !== "") {
    requestHeaders.append("Authorization", "Bearer " + authToken);
  }
  setLoadingStatus(true);
  //send request
  const checkResponse = await fetch(process.env.REACT_APP_JAVA_API_URL + apiPath, {
    method: requestMethod,
    mode: "cors",
    headers: requestHeaders,
    body: requestMethod === "GET" ? null : requestBody,
    signal: controller.signal,
  });

  if (checkResponse.status !== 200) {
    setLoadingStatus(false);
    //redirect back to login screen when status is 401 (unauthorised) or 500 (internal server error)
    if (checkResponse.status === 401 || checkResponse.status === 500) {
      // window.location.href = "/";
    }
    return {
      success: false,
      message: "Sorry, something went wrong",
    };
  } else {
    setLoadingStatus(false);
    let response = {};
    if (requestMethod !== "DELETE") {
      response = await checkResponse.json();
    }
    return {
      success: true,
      response: response,
    };
  }
};

export const cancelOngoingRequests = () => {
  controller.abort();
  setLoadingStatus(false);
  setBatchLoadingStatus(false);
  store.dispatch(updateStateData({
    show_loading_cancel_button: false,
  }));
};

//common function to send api request and returning the response back to the calling function
export const sendApiRequest = async (apiPath, requestBody, requestMethod, loaderControl = false) => {
  // re-initialize the controller if the previous request was aborted
  if (controller.signal.aborted) {
    controller = new AbortController();
  }
  let authToken = fetchToken();
  let requestHeaders = new Headers();
  requestHeaders.append("Content-Type", "application/json");

  if (authToken !== "") {
    requestHeaders.append("Authorization", "Bearer " + authToken);
  }

  let retry = false;
  let retryCount = 0;
  // if api call fails in first attempt, system will retry the api one more time.
  do {
    setLoadingStatus(true);
    // api request
    const checkResponse = await fetch(process.env.REACT_APP_JAVA_API_URL + apiPath, {
      method: requestMethod,
      mode: "cors",
      headers: requestHeaders,
      body: requestMethod === "GET" ? null : JSON.stringify(requestBody),
      signal: controller.signal,
    });
    // response check
    if (checkResponse.status === 401 || checkResponse.status === 500) {
      retry = true;
      retryCount++;

      if (retryCount > 1) {
        // If retry fails, redirect to login page
        setLoadingStatus(false);
        // window.location.href = "/";
        return {
          success: false,
          message: "Unauthorized. Redirecting to login page...",
        };
      } else {
        // Retry after fetching a new token
        authToken = fetchToken();
        requestHeaders.set("Authorization", "Bearer " + authToken);
      }
    } else if (checkResponse.status !== 200 && checkResponse.status !== 201) {
      setLoadingStatus(false);
      // Handle other error statuses
      return {
        success: false,
        message: "Sorry, something went wrong",
      };
    } else {
      // Reset retry on successful response
      retry = false;

      if (loaderControl === false) {
        setLoadingStatus(false);
      }
      let response = {};
      if (requestMethod !== "DELETE") {
        response = await checkResponse.json();
      }

      return {
        success: typeof response.error !== "undefined" && response.error === true ? false : true,
        response: response,
      };
    }
  } while (retry);
};

// fetch workspace list for the logged user
export const fetchWorkSpaceList = async (navigate) => {
  const fetchWorkSpaceDetail = await sendApiRequest("workspaces", {}, "GET");
  //check if data variable has token or not
  if (typeof fetchWorkSpaceDetail.success !== "undefined" && fetchWorkSpaceDetail.success === true) {
    const workSpaceItems = fetchWorkSpaceDetail.response;
    const showWorkspaceList = workSpaceItems.length > 1 ? true : false;
    // show popup
    store.dispatch(
      updateStateData({
        show_workspace_list: showWorkspaceList,
        workspace_options: workSpaceItems,
      })
    );
    createCookie("workspace_options", JSON.stringify(workSpaceItems));
    // let the user go to it's default workspace
    if (showWorkspaceList === false) {
      checkAuthorizedUserDetails(navigate);
    }
  } else {
    return {
      success: false,
    };
  }
};

export const findUsersAccesstype = () => {
  const currentStateData = store.getState().userSelections;
  const checkAccesstypeValues = checkAccessTypeFromCookie();
  let userAccessTypeName = ""

  if (typeof checkAccesstypeValues.access_types !== "undefined") {
    let userAccessType = checkAccesstypeValues.access_types.access_types.length > 1 ? checkAccesstypeValues.access_types.access_types.filter(accessName => accessName.access_type !== "brand") : checkAccesstypeValues.access_types.access_types;

    userAccessTypeName = userAccessType[0].access_type
  }
  return userAccessTypeName;
}

export const checkAuthorizedUserDetails = async (navigate, usedFor, accessSelection) => {
  const accessType = typeof accessSelection !== "undefined" ? accessSelection : findUsersAccesstype();
  let endPoint = "users/info?access_type=" + accessType
  const fetchUserDetail = await sendApiRequest(endPoint, {}, "GET");

  if (typeof fetchUserDetail.success !== "undefined" && fetchUserDetail.success === true) {
    /*save permissions in the cookies, so we can use it even after page refresh. 
      We can't save it to redux because redux state will get reset after page reload.*/

    const userDetail = {
      user_email: fetchUserDetail.response.email,
      user_name: fetchUserDetail.response.username,
      user_role: fetchUserDetail.response.roles[0],
      assigned_user_roles: fetchUserDetail.response.roles,
      user_role_info: fetchUserDetail.response.role_info.sort((next, prev) => {
        return next.role_name.localeCompare(prev.role_name);
      }).map((role) => {
        return { role_name: role.role_name, description: role.description }
      }),
      force_reset_password: false,
      current_workspace: fetchUserDetail.response.assigned_company,
      user_type: fetchUserDetail.response.user_type,
      root_company_id: fetchUserDetail.response.root_company_id,
      assigned_company: fetchUserDetail.response.assigned_company,
      deactivated_sections: fetchUserDetail.response.deactivated_sections,
      loop_visual_limit: fetchUserDetail.response.loop_visual_limit,
      loop_visual_max_duration: fetchUserDetail.response.loop_visual_max_duration,
    };

    createCookie("userPermissions", JSON.stringify(fetchUserDetail.response.permissions));
    createCookie("userRole", JSON.stringify(userDetail.user_role));
    createCookie("userDetail", JSON.stringify(userDetail));

    createCookie("current_workspace", JSON.stringify(userDetail.current_workspace));

    store.dispatch(updateStateData(userDetail));

    const landingPage = await allowedNavOptions({ pathname: "/" });
    let redirectTo = "/cap-overview";
    if (typeof landingPage.navItems !== "undefined" && landingPage.navItems.length !== 0) {
      if (landingPage.navItems[0].nested_list === true) {
        redirectTo = landingPage.navItems[0].nested_items[0].path
      } else {
        redirectTo = landingPage.navItems[0].path
      }
    }
    //check whether we need to force user to reset the password
    if (userDetail.force_reset_password === true) {
      redirectTo = "/accounts/reset-password";
    }

    if (window.location.pathname === redirectTo) {
      // this condition is added to make sure that correct workspace changes reflect properly
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      // reload webpage to clear out all redux values
      if (usedFor === "update") {
        setTimeout(() => {
          navigate(redirectTo);
          window.location.reload();
        }, 2000);
      } else {
        navigate(redirectTo);
      }
    }

    return {
      success: true,
      user_detail: userDetail,
    };
  } else {
    window.location.href("/");
    return {
      success: false,
    };
  }
};

// Authentication (API will return a token if provided credentials are valid.)
export const authorizeCredentials = async (userCredentials, navigate) => {
  const authenticateUser = await sendApiRequest("authenticate", userCredentials, "POST", true);
  //check if data variable has token or not
  if (typeof authenticateUser.success !== "undefined" && authenticateUser.success === true) {
    /*save token in the cookies, so we can use it even after page refresh. 
      We can't save it to redux because redux state will get reset after page reload.*/
    createCookie("authToken", authenticateUser.response.access_token);
    // fetch user details and save user permissions in cookies (cookies are preferred to handle use cases related to page refresh)
    setTimeout(() => {
      // check user access type i.e., either brand or retailer or both
      checkAccessType().then((info) => {
        fetchWorkSpaceList(navigate);
      });

    }, 1000);
  } else {
    setLoadingStatus(false);
    toast("Please check your username and password and try again.", { type: "error" });
  }
};

export const checkAccessType = async () => {
  const accessTypes = await sendApiRequest("users/access", {}, "GET");
  //check if data variable has token or not
  if (typeof accessTypes.success !== "undefined" && accessTypes.success === true) {
    const accessItems = accessTypes.response;
    const hasMultipleAccessTypes = accessItems.length > 1 ? true : false;
    const accessRespObj = {
      has_multiple_access: hasMultipleAccessTypes,
      access_types: accessItems,
    }
    // show popup
    store.dispatch(
      updateStateData(accessRespObj)
    );
    createCookie("access_types", JSON.stringify(accessRespObj));
  } else {
    return {
      success: false,
    };
  }
}

// to generate token for embeddable authorization.

export const generateEmbeddableToken = async (requestBody) => {
  setLoadingStatus(true);
  const embeddableEnv = findEmbeddableEnvironmentInfo();
  let environment = embeddableEnv.environment;
  let savedVersion = embeddableEnv.savedVersion;

  const tokenResponse = await sendApiRequest("api/embeddable/authenticate", {
    "embeddable_id": requestBody.embeddableId,
    "environment": environment,
    "saved_version": savedVersion,
    "number_root": requestBody.securityContext.number_root
  }, "POST");

  const response = tokenResponse.response;
  return {
    success: true,
    response: response.token,
  };
};

export const findEmbeddableEnvironmentInfo = () => {
  let environment = process.env.REACT_APP_ENV_NAME === "prod" ? "prod" : process.env.REACT_APP_ENV_NAME === "demo" ? "demo" : "staging";
  let savedVersion = process.env.REACT_APP_ENV_NAME === "prod" ? "production" : process.env.REACT_APP_ENV_NAME === "demo" ? "development" : "staging";
  return {
    environment: environment,
    savedVersion: savedVersion,
  }
};